import { useEffect, useState } from 'react';
import type { EstadoPreguntaSinRespuesta, PreguntaSinRespuesta } from '../../api/ops/asistente';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { PanelLateral } from '../../componentes/PanelLateral';
import { avisoBreveDeError } from '../../logica/estados';
import type { PropsApartado } from './comun';
import { autorDe, fechaCorta, FILTROS_SIN_RESPUESTA, lineaDeEstado, lineaDeVeces, nombreIdioma } from './reglas';

/**
 * «Preguntas sin respuesta» (11.6, G720): lo que el asistente no supo responder sobre el negocio, agrupado y sin datos
 * personales (`listAssistantQuestions`, por defecto las `open`, de la más preguntada a la menos). Desde cada una se abre
 * el panel de pregunta frecuente con el texto puesto («Responder») y se marca como respondida o ignorada
 * (`updateAssistantQuestion` con `If-Match`); las respondidas o ignoradas se vuelven a abrir. `?pregunta=aqn_…` abre una
 * (`getAssistantQuestion`, destino de los enlaces y avisos). Quién la cambió, con su nombre guardado (G719).
 */
const POR_PAGINA = 20;

export function SinRespuesta({ datos, ops, negocio, movil, busqueda, navegar, ruta, onResponder }: PropsApartado & { onResponder: (texto: string, idioma: string | null) => void }) {
    const avisar = useAvisoBreve();
    const [filtro, setFiltro] = useState<EstadoPreguntaSinRespuesta>('open');
    const [lista, setLista] = useState<{ filtro: string; items: PreguntaSinRespuesta[]; siguiente: string | null } | null>(null);
    const [intento, setIntento] = useState(0);
    const [ocupada, setOcupada] = useState<string | null>(null);
    const [abierta, setAbierta] = useState<PreguntaSinRespuesta | null>(null);
    const pedida = busqueda.get('pregunta');

    useEffect(() => {
        let vivo = true;
        ops.listAssistantQuestions(negocio, { status: filtro, sort: '-occurrences', limit: POR_PAGINA })
            .then((r) => { if (vivo) { setLista({ filtro, items: r.data, siguiente: r.next_cursor }); } })
            .catch(() => { if (vivo) { setLista({ filtro, items: [], siguiente: null }); } });
        return () => { vivo = false; };
    }, [ops, negocio, filtro, intento]);

    useEffect(() => {
        if (pedida === null || pedida === '') {
            return;
        }
        let vivo = true;
        ops.getAssistantQuestion(negocio, pedida)
            .then((r) => { if (vivo) { setAbierta(r.datos); } })
            .catch(() => undefined);
        return () => { vivo = false; };
    }, [ops, negocio, pedida]);

    const cerrarPanel = () => {
        setAbierta(null);
        if (pedida !== null) {
            const q = new URLSearchParams(busqueda);
            q.delete('pregunta');
            const base = ruta.split('?')[0] ?? ruta;
            navegar(q.toString() === '' ? base : `${base}?${q.toString()}`, true);
        }
    };

    /** Cambia el estado con la versión leída: si otra persona la cambió antes, se avisa y se vuelve a leer. */
    const conflicto = () => {
        avisar({ tipo: 'error', texto: 'Otra persona ha cambiado esta pregunta. Te enseñamos lo último.' });
        setAbierta(null);
        setIntento((i) => i + 1);
    };
    const cambiar = async (p: PreguntaSinRespuesta, status: EstadoPreguntaSinRespuesta) => {
        setOcupada(p.id);
        try {
            const leida = await ops.getAssistantQuestion(negocio, p.id);
            if (leida.datos.version !== p.version) {
                conflicto();
                return;
            }
            const r = await ops.updateAssistantQuestion(negocio, p.id, status, leida.version ?? '');
            avisar({ tipo: 'ok', texto: status === 'answered' ? 'Marcada como respondida.' : status === 'ignored' ? 'Pregunta ignorada.' : 'Pregunta abierta otra vez.' });
            setLista((l) => (l === null ? l : { ...l, items: l.items.filter((x) => x.id !== p.id || r.datos.status === l.filtro) }));
            if (abierta?.id === p.id) {
                setAbierta(r.datos);
            }
        } catch (e) {
            if (e instanceof ErrorApi && e.codigo === 'version_conflict') {
                conflicto();
            } else {
                avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido cambiar. Inténtalo otra vez.' });
            }
        } finally {
            setOcupada(null);
        }
    };

    const actual = lista?.filtro === filtro ? lista : null;
    const acciones = (p: PreguntaSinRespuesta, enPanel: boolean) =>
        !datos.editar ? null : p.status === 'open' ? (
            <>
                <Boton tipo="secundario" icono="plus" movil={movil} onClick={() => { if (enPanel) { cerrarPanel(); } onResponder(p.text, p.locale ?? null); }}>
                    Responder
                </Boton>
                <Boton tipo="secundario" icono="check" movil={movil} ocupado={ocupada === p.id} ocupadoTexto="Guardando…" onClick={() => { void cambiar(p, 'answered'); }}>
                    Ya está respondida
                </Boton>
                <Boton tipo="fantasma" icono="x" movil={movil} disabled={ocupada === p.id} onClick={() => { void cambiar(p, 'ignored'); }}>
                    Ignorar
                </Boton>
            </>
        ) : (
            <Boton tipo="secundario" icono="rotate-ccw" movil={movil} ocupado={ocupada === p.id} ocupadoTexto="Guardando…" onClick={() => { void cambiar(p, 'open'); }}>
                Volver a abrir
            </Boton>
        );

    const linea = (p: PreguntaSinRespuesta) =>
        p.status === 'open' ? lineaDeVeces(p.occurrences, p.last_asked_at) : `${lineaDeVeces(p.occurrences, p.last_asked_at)} · ${lineaDeEstado(p.status, autorDe(p.status_changed_by_member_id, p.status_changed_by_name, datos.equipo), p.status_changed_at)}`;

    return (
        <section className="rv-ca-bloque" aria-label="Preguntas sin respuesta">
            <div className="rv-ca-bloque__cab">
                <p className="rv-ca-ceja">Preguntas sin respuesta</p>
                <span>Lo que te han preguntado y el asistente no sabía. Añádelo a tus preguntas frecuentes para que la próxima vez conteste.</span>
            </div>
            <Filtros etiqueta="Preguntas sin respuesta" items={FILTROS_SIN_RESPUESTA.map((f) => ({ etiqueta: f.etiqueta }))} valor={FILTROS_SIN_RESPUESTA.findIndex((f) => f.valor === filtro)} onCambio={(i) => { setFiltro(FILTROS_SIN_RESPUESTA[i]?.valor ?? 'open'); }} />
            {actual === null ? null : actual.items.length === 0 ? (
                <p className="rv-ca-nota rv-ca-nota--gris">{filtro === 'open' ? 'Ninguna por ahora: el asistente ha sabido responder a todo.' : filtro === 'answered' ? 'Ninguna respondida todavía.' : 'Ninguna ignorada.'}</p>
            ) : (
                <div className="rv-ca-lista">
                    {actual.items.map((p) => (
                        <div key={p.id} className="rv-ca-fila-caja">
                            <div className="rv-ca-fila">
                                <span className="rv-ca-baldosa">
                                    <Icono nombre="circle-help" />
                                </span>
                                <span className="rv-ca-fila__texto">
                                    <button type="button" className="rv-as-enlace" onClick={() => { setAbierta(p); }}>
                                        <strong>{p.text}</strong>
                                    </button>
                                    <span>{linea(p)}</span>
                                </span>
                            </div>
                            {datos.editar && <div className="rv-as-botones">{acciones(p, false)}</div>}
                        </div>
                    ))}
                </div>
            )}

            <PanelLateral abierto={abierta !== null} onCerrar={cerrarPanel} movil={movil} eyebrow="Pregunta sin respuesta" icono="circle-help" titulo={abierta?.text ?? ''}>
                {abierta !== null && (
                    <div className="rv-as-panel">
                        <div className="rv-cn-hechos">
                            <div className="rv-cn-hecho">
                                <span>Veces</span>
                                <strong>{abierta.occurrences.toLocaleString('es-ES')}</strong>
                            </div>
                            <div className="rv-cn-hecho">
                                <span>Primera vez</span>
                                <strong>{fechaCorta(abierta.first_asked_at)}</strong>
                            </div>
                            <div className="rv-cn-hecho">
                                <span>Última vez</span>
                                <strong>{fechaCorta(abierta.last_asked_at)}</strong>
                            </div>
                            {abierta.locale != null && (
                                <div className="rv-cn-hecho">
                                    <span>Idioma</span>
                                    <strong>{nombreIdioma(abierta.locale)}</strong>
                                </div>
                            )}
                            {abierta.status !== 'open' && (
                                <div className="rv-cn-hecho">
                                    <span>Estado</span>
                                    <strong>{lineaDeEstado(abierta.status, autorDe(abierta.status_changed_by_member_id, abierta.status_changed_by_name, datos.equipo), abierta.status_changed_at)}</strong>
                                </div>
                            )}
                        </div>
                        <small className="rv-as-ayuda">El asistente la apunta sin datos personales. Si una respondida se vuelve a preguntar, se abre otra vez.</small>
                        {datos.editar && <div className="rv-as-botones">{acciones(abierta, true)}</div>}
                    </div>
                )}
            </PanelLateral>
        </section>
    );
}
